import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase } from '../supabase';
import {
  ArrowRight,
  Loader2,
  CheckCircle2,
  ArrowLeft,
  AlertCircle,
  Sparkles,
  ShieldCheck,
  Store,
  Phone,
  ShoppingBag,
  Utensils,
  Briefcase,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { usePublicPlans } from '../hooks/usePublicPlans';
import { ALL_MODULES } from '../utils/organizationModules';
import {
  BUSINESS_PRESETS,
  completeFirstRunOnboarding,
  ensureCurrentUserOrgMembership,
  ensureCurrentUserProfileMembership,
  ensureOrgOperationalDefaults,
  finalizeFirstRunOnboarding,
  DEFAULT_TRIAL_DURATION_DAYS,
} from '../utils/onboarding';
import { startFirstSaleOnboarding } from '../utils/firstSaleOnboarding';
import { trackOnboardingEvent } from '../utils/onboardingTelemetry';
import { getAttribution, trackTrialStarted } from '../utils/metaPixel';
import '../components/public/publicSite.css';


// Icon por preset para el selector compacto.
const PRESET_ICON_MAP = {
  comercio: Store,
  indumentaria: ShoppingBag,
  gastronomia: Utensils,
  servicios: Briefcase,
};
const DEFAULT_BUSINESS_PRESET = 'comercio';

/* ── Gastify "G" mark ── */
function GastifyMark({ size = 44 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="gst-reg-plan-bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#10B981" />
          <stop offset="100%" stopColor="#059669" />
        </linearGradient>
      </defs>
      <rect width="100" height="100" rx="22" fill="url(#gst-reg-plan-bg)" />
      <circle cx="50" cy="50" r="24" fill="none" stroke="white" strokeWidth="9"
        strokeLinecap="round" strokeDasharray="108 43"
        transform="rotate(-55 50 50)" />
      <rect x="50" y="46" width="20" height="8" rx="4" fill="white" />
    </svg>
  );
}

const DEFAULT_ROLE_NAMES = ['cajero', 'vendedor', 'operario', 'encargado', 'admin', 'administrador'];
// Trial: fuente unica compartida (F9). El fallback local de 14 se elimino; ahora
// viene de utils/onboarding (DEFAULT_TRIAL_DURATION_DAYS). app_settings/public
// siguen teniendo prioridad si estan configurados.
const DEFAULT_TRIAL_DAYS = DEFAULT_TRIAL_DURATION_DAYS;
const RECOMMENDED_PLAN_KEY = 'premium';
// F2 — Seeds unificados: las categorias, unidades, medios de cobro y comprobantes
// ya NO se siembran aca. completeFirstRunOnboarding (utils/onboarding.js) es la
// unica fuente de verdad para esas 4 tablas, sembrando el set del preset elegido.


const getOrganizationErrorMsg = (error) => {
  const combined = `${error?.message || ''} ${error?.details || ''}`.toLowerCase();
  if (combined.includes('null value in column "email"')) return 'Falta un email de contacto.';
  if (combined.includes('violates not-null constraint')) return 'Falta un dato obligatorio.';
  if (combined.includes('duplicate key') || combined.includes('unique constraint')) return 'Ya existe una empresa con esos datos.';
  return 'No pudimos crear tu empresa en este momento. Intentá nuevamente.';
};

/**
 * RegistroOnboarding · Paso 2 de 2
 * --------------------------------------
 * Llega acá un usuario YA registrado en supabase.auth (paso 1).
 * Se eligen 3 cosas como mucho:
 *   1. Plan (Básico / Premium / Enterprise) — Premium viene por default.
 * No se pide nada más para no agregar fricción. El trial corre 14 días sí o sí.
 *
 * Cuando confirma, esta pantalla:
 *   · Crea la organization con status='trial' y trial_ends_at = now + N días
 *   · Vincula el profile/users a la org
 *   · Provisiona datos base minimos en las tablas operativas
 *   · Redirige al onboarding interno para elegir perfil de negocio
 *
 * Si el usuario refresca y todavía no tiene organization, le mostramos
 * esta pantalla otra vez. Si ya tiene una organización, lo mandamos a /sistema.
 */
export default function RegistroOnboardingPlan() {
  const navigate = useNavigate();
  const location = useLocation();
  const isProcessing = useRef(false);

  const [sessionUser, setSessionUser] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [datosNegocio, setDatosNegocio] = useState(null);
  // Cuando datosNegocio viene null (usuario vuelve tras meses sin org), pedimos
  // el mínimo antes de mostrar el selector de plan en lugar de crear silenciosamente
  // una org "Mi negocio" sin email útil.
  const [needsBusinessForm, setNeedsBusinessForm] = useState(false);
  const [businessFormData, setBusinessFormData] = useState({ nombreNegocio: '', telefono: '' });
  // Plan fijo: el alta arranca siempre en premium (trial completo). La selección
  // visual de plan se quitó del paso 2; se decide al contratar, no al registrarse.
  const planKey = RECOMMENDED_PLAN_KEY;
  // Tipo de negocio: ahora se elige acá en vez de en un wizard separado post-registro.
  const [businessPresetKey, setBusinessPresetKey] = useState(DEFAULT_BUSINESS_PRESET);
  const [provisioning, setProvisioning] = useState(false);
  const [provisioningStatus, setProvisioningStatus] = useState({ auth: false, org: false, final: false });
  const [loadingMsg, setLoadingMsg] = useState('Preparando tu espacio de trabajo...');
  const [errorMsg, setErrorMsg] = useState(null);

  const [presets, setPresets] = useState({});
  const [loadingPresets, setLoadingPresets] = useState(true);

  // Cargar presets de negocio desde base de datos
  useEffect(() => {
    const loadPresets = async () => {
      try {
        const { data, error } = await supabase
          .from('app_settings')
          .select('business_presets')
          .eq('id', 'global')
          .maybeSingle();

        if (!error && data?.business_presets && typeof data.business_presets === 'object' && Object.keys(data.business_presets).length > 0) {
          let merged = {};
          const dbPresets = data.business_presets;
          const keys = Object.keys(dbPresets);
          for (const k of keys) {
            merged[k] = {
              ...BUSINESS_PRESETS[k], // fallback styling/details local
              ...dbPresets[k],
              flags: {
                ...(BUSINESS_PRESETS[k]?.flags || {}),
                ...(dbPresets[k]?.flags || {})
              }
            };
          }
          setPresets(merged);
        } else {
          setPresets(BUSINESS_PRESETS);
        }
      } catch (err) {
        console.warn('[RegistroOnboardingPlan] Error loading dynamic presets:', err);
        setPresets(BUSINESS_PRESETS);
      } finally {
        setLoadingPresets(false);
      }
    };

    loadPresets();
  }, []);


  const { plans, trialDays: publicTrialDays } = usePublicPlans();
  const formattedTrialDays = Number(publicTrialDays) || DEFAULT_TRIAL_DAYS;

  /* ── 1. Validar sesión y traer datos del negocio del paso 1 ── */
  useEffect(() => {
    let alive = true;

    const init = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        if (error || !data?.session?.user) {
          // No hay sesión: volver al paso 1
          if (alive) navigate('/registro-onboarding', { replace: true });
          return;
        }

        if (!alive) return;
        setSessionUser(data.session.user);

        // Si ya tiene organization vinculada, mandar a sistema.
        // Revisamos ambas tablas porque algunas instancias usan profiles y otras public.users.
        for (const tableName of ['profiles', 'users']) {
          try {
            const { data: existing } = await supabase
              .from(tableName)
              .select('organization_id')
              .eq('id', data.session.user.id)
              .maybeSingle();
            if (existing?.organization_id) {
              if (alive) navigate('/sistema', { replace: true });
              return;
            }
          } catch {
            /* probar siguiente tabla */
          }
        }

        // H1 — hito del embudo: llegó al paso 2 (rubro) sin organización creada.
        // Complementa a 'auth_signup' del paso 1: la diferencia entre ambos mide
        // el drop de la confirmación de email.
        trackOnboardingEvent({ event: 'plan_step_viewed', userId: data.session.user.id });

        // Recuperar datos del negocio que dejó el paso 1
        // Primero sessionStorage (misma pestaña), después localStorage (vino
        // de confirmar email desde otra pestaña o más tarde).
        let recovered = null;
        try {
          const raw =
            window.sessionStorage.getItem('gastify:registro-onboarding:datos-negocio') ||
            window.localStorage.getItem('gastify:registro-onboarding:datos-negocio');
          if (raw) recovered = JSON.parse(raw);
        } catch {
          /* sessionStorage/localStorage puede no estar disponible */
        }

        // Fallback: si no hay datos en localStorage/sessionStorage, recuperar de user_metadata
        if (!recovered?.nombreNegocio && data.session.user) {
          const meta = data.session.user.user_metadata || {};
          if (meta.business_name || meta.nombreNegocio) {
            recovered = {
              nombreUsuario: meta.full_name || '',
              nombreNegocio: meta.business_name || meta.nombreNegocio || '',
              telefono: meta.phone || '',
              email: data.session.user.email || '',
            };
          }
        }

        if (recovered?.nombreNegocio) {
          if (alive) setDatosNegocio(recovered);
        } else if (alive) {
          // Caso "huérfano" real: user existe en auth pero no hay datos del negocio ni en metadatos.
          // Suele pasar cuando la org fue limpiada por housekeeping y vuelve meses después.
          // Pedimos el mínimo antes de crear una org genérica.
          const meta = data.session.user.user_metadata || {};
          setBusinessFormData({
            nombreNegocio: '',
            telefono: meta.phone || '',
          });
          setNeedsBusinessForm(true);
        }
      } catch (err) {
        console.error('[RegistroOnboardingPlan] init error:', err);
      } finally {
        if (alive) setLoadingSession(false);
      }
    };

    init();
    return () => { alive = false; };
  }, [navigate]);

  const selectedPlan = useMemo(
    () => plans.find((plan) => plan.key === planKey) || plans[0] || null,
    [planKey, plans],
  );

  /* ── 2. Provisioning helpers ── */
  // H5 — El provisioning de piezas base (sitio, cliente, condición de pago,
  // lista de precios, configs) vive en utils/onboarding.js como
  // ensureOrgOperationalDefaults: idempotente, así un alta interrumpida se
  // completa al reintentar acá o al entrar a Sistema (resumePendingProvisioning).

  const seedOrgRoleTemplates = async ({ orgId, moduleIds }) => {
    const normalizedModules = [...new Set((moduleIds || []).filter(Boolean))];
    if (!orgId || normalizedModules.length === 0) return;
    const rows = DEFAULT_ROLE_NAMES.flatMap((roleName) =>
      normalizedModules.map((moduleId) => ({
        organization_id: orgId,
        role_name: roleName,
        module_id: moduleId,
      }))
    );
    const { error } = await supabase.from('role_templates').insert(rows);
    if (error) throw error;
  };

  /* ── 3. Submit: crea org + vincula profile + provisiona base ── */
  const handleConfirmPlan = async () => {
    if (isProcessing.current || !sessionUser) return;
    isProcessing.current = true;
    setProvisioning(true);
    setErrorMsg(null);

    try {
      setLoadingMsg('Activando tu prueba gratis...');
      setProvisioningStatus({ auth: true, org: false, final: false });

      // Resolver días de trial: app_settings → public → default
      let trialDays = formattedTrialDays;
      try {
        const { data: settings } = await supabase
          .from('app_settings')
          .select('default_trial_days')
          .eq('id', 'global')
          .maybeSingle();
        if (settings?.default_trial_days && Number.isFinite(Number(settings.default_trial_days))) {
          trialDays = Number(settings.default_trial_days);
        }
      } catch (settingsErr) {
        console.warn('Fallback a default_trial_days local.', settingsErr);
      }

      // Estrategia unificada: Todos los comercios tienen acceso TOTAL a todos los módulos desde el día 1
      const selectedModules = ALL_MODULES.map((m) => m.id);
      const trialStartsAt = new Date();
      const trialEndsAt = new Date(trialStartsAt.getTime() + trialDays * 24 * 60 * 60 * 1000);

      const negocioFinal = datosNegocio || {};
      const orgName = negocioFinal.nombreNegocio || 'Mi negocio';
      const orgEmail = negocioFinal.email || sessionUser.email || '';
      const orgPhone = negocioFinal.telefono || '';
      const fullName = negocioFinal.nombreUsuario || sessionUser.user_metadata?.full_name || '';

      setLoadingMsg('Creando tu empresa...');

      const { data: orgData, error: orgError } = await supabase
        .from('organizations')
        .insert([{
          name: orgName,
          email: String(orgEmail).trim().toLowerCase(),
          plan: planKey,
          current_plan_key: planKey,
          status: 'trial',
          subscription_status: 'trial',
          phone: orgPhone,
          business_type: businessPresetKey,
          onboarding_status: 'pending',
          custom_modules: selectedModules,
          // Gate de cohorte: solo las altas nuevas encienden el onboarding v2.
          // Las orgs ya creadas quedan en false y no ven ningun cambio de UX.
          onboarding_v2_enabled: true,
          // Los tours por modulo NO se siembran en el alta. Saltaban solos la
          // primera vez que el usuario entraba a cada modulo (dashboard, POS,
          // productos, clientes, inventario) con textos genericos, encima del
          // recorrido de bienvenida. Ensenar cada pantalla es trabajo del
          // checklist "Empeza a operar", donde cada paso tiene su "Iniciar
          // guia" y lo dispara el usuario cuando quiere.
          module_onboarding_statuses: {},
          billing_branch_count: 1,
          billing_user_count: 1,
          trial_starts_at: trialStartsAt.toISOString(),
          trial_ends_at: trialEndsAt.toISOString(),
        }])
        .select('id, name')
        .single();

      if (orgError) throw new Error(getOrganizationErrorMsg(orgError));

      // 1.5 — hito del embudo: alta de la org (registro completado).
      trackOnboardingEvent({
        event: 'signup',
        organizationId: orgData.id,
        userId: sessionUser.id,
        metadata: { plan: planKey, businessType: businessPresetKey, ...getAttribution() },
      });
      // Meta Pixel: alta completa con prueba gratis en marcha (evento que optimiza la campaña).
      trackTrialStarted({ plan: planKey });

      setProvisioningStatus((prev) => ({ ...prev, org: true }));
      setLoadingMsg('Configurando tu equipo y módulos...');

      const linkEmail = String(orgEmail).trim().toLowerCase();

      // Refrescar metadata de auth (best-effort; alimenta el fallback de JWT).
      await supabase.auth.updateUser({
        data: { full_name: fullName, role: 'admin', organization_id: orgData.id },
      }).catch(() => {});

      // Vinculo usuario <-> empresa con el MISMO patron que Impulza, sin depender
      // del RPC admin_ensure_profile:
      //   1) update directo de profiles (su RLS esta deshabilitada),
      //   2) ensureCurrentUserProfileMembership (reintentos sobre profiles),
      //   3) ensureCurrentUserOrgMembership sobre public.users, que es la fuente de
      //      verdad de get_my_org_id() y cuyo RLS permite el self-link (id = auth.uid()).
      // Sin el paso 3 los INSERT de las tablas semilla del provisioning fallan el RLS.
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ organization_id: orgData.id, role: 'admin', full_name: fullName })
        .eq('id', sessionUser.id);
      if (profileError) {
        console.warn('Update profile error (RLS?):', profileError.message);
      }

      await ensureCurrentUserProfileMembership({
        userId: sessionUser.id,
        organizationId: orgData.id,
        email: linkEmail,
        fullName,
        role: 'admin',
      });

      await ensureCurrentUserOrgMembership({
        userId: sessionUser.id,
        organizationId: orgData.id,
        email: linkEmail,
        fullName,
        role: 'admin',
      });

      setLoadingMsg('Preparando tu onboarding inicial...');
      await seedOrgRoleTemplates({ orgId: orgData.id, moduleIds: selectedModules });
      await ensureOrgOperationalDefaults({ organizationId: orgData.id, orgName: orgData.name });

      // ── Aplicar preset de negocio en el mismo paso ────────────────
      // Antes era un wizard separado (/sistema/onboarding). Para evitar
      // el 4to wizard encadenado, lo hacemos acá y vamos derecho a la
      // primera venta. Si falla algo, no rompemos el alta: cae al wizard.
      let presetApplied = false;
      try {
        setLoadingMsg(`Encendiendo módulos para ${businessPresetKey}...`);
        await completeFirstRunOnboarding({
          organizationId: orgData.id,
          actorUserId: sessionUser.id,
          businessType: businessPresetKey,
        });
        await finalizeFirstRunOnboarding({
          organizationId: orgData.id,
          actorUserId: sessionUser.id,
        });
        presetApplied = true;
        // 1.5 — hito del embudo: provisioning completo (base operativa lista).
        trackOnboardingEvent({
          event: 'provisioned',
          organizationId: orgData.id,
          userId: sessionUser.id,
          metadata: { businessType: businessPresetKey },
        });
      } catch (presetErr) {
        console.warn('[RegistroOnboardingPlan] preset apply failed:', presetErr);
      }

      setLoadingMsg('¡Listo!');
      setProvisioningStatus({ auth: true, org: true, final: true });

      // Marcamos status como visto para no caer en el intersticial
      try {
        window.sessionStorage.setItem('hasSeenStatus', 'true');
      } catch { /* no-op */ }

      // Limpiar datos del negocio en sessionStorage y localStorage
      try {
        window.sessionStorage.removeItem('gastify:registro-onboarding:datos-negocio');
        window.localStorage.removeItem('gastify:registro-onboarding:datos-negocio');
      } catch { /* no-op */ }

      // Si el preset se aplicó OK, salteamos /sistema/onboarding (ya eligió el
      // rubro en el alta, ese wizard se lo volvería a preguntar) y caemos en el
      // dashboard con el recorrido de bienvenida arrancado.
      //
      // El recorrido es el mismo que ofrece el "?" de la barra superior, y son
      // tres pasos: ubicarse en la pantalla, ver dónde vive "Empezá a operar" y
      // elegir si quiere las guías o explorar solo. Ya no arrastra al usuario a
      // crear un producto simulado, que era lo que lo hacía sentir una guía
      // impuesta apenas terminaba el alta.
      //
      // Antes se caía directo en /sistema/progreso: el usuario abría la cuenta y
      // lo primero que veía era una lista de 16 tareas pendientes, sin haber
      // visto el sistema.
      if (presetApplied) {
        startFirstSaleOnboarding({
          organizationId: orgData.id,
          userId: sessionUser.id,
        });
        setTimeout(() => { window.location.href = '/sistema/dashboard'; }, 900);
      } else {
        setTimeout(() => { window.location.href = '/sistema/onboarding'; }, 900);
      }
    } catch (err) {
      console.error('[RegistroOnboardingPlan] handleConfirmPlan error:', err);
      setErrorMsg(err.message);
      toast.error(err.message, { duration: 7000 });
      setProvisioning(false);
      isProcessing.current = false;
    }
  };

  /* ── 4. Render ── */
  if (loadingSession || loadingPresets) {
    return (
      <div className="gst-site-tokens h-screen w-full grid place-items-center bg-[var(--gst-cream-50)]">
        <Loader2 size={28} className="animate-spin text-[var(--gst-mint-600)]" />
      </div>
    );
  }

  return (
    <div className="gst-site-tokens min-h-screen w-full max-w-full overflow-y-auto bg-[var(--gst-cream-50)] text-[var(--gst-graphite-900)] flex flex-col md:p-3" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700;9..144,800&family=Manrope:wght@400;500;600;700;800&display=swap');
        .gst-public-display { font-family: 'Fraunces', Georgia, serif; }
      `}</style>

      <div className="w-full max-w-5xl mx-auto px-4 pt-4 pb-1 flex items-center justify-between z-20">
        <button
          onClick={() => navigate('/registro-onboarding')}
          className="flex items-center justify-center gap-2 rounded-full border border-[var(--gst-border-strong)] bg-[var(--surface)] px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] text-[var(--gst-graphite-700)] shadow-sm transition hover:border-[var(--gst-mint-300)] hover:text-[var(--gst-mint-700)]"
        >
          <ArrowLeft size={14} /> Volver
        </button>
      </div>

      <div className="flex-1 flex items-start justify-center py-2 px-4">
        <div className="w-full max-w-5xl">
          {/* Header */}
          <div className="text-center mb-4">
            <div className="inline-flex items-center gap-2 rounded-full bg-[var(--gst-mint-100)] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-[var(--gst-mint-700)] mb-2">
              <Sparkles size={14} />
              Paso 2 de 2
            </div>
            <div className="flex justify-center mb-2">
              <GastifyMark size={44} />
            </div>
            <h1 className="gst-public-display text-2xl md:text-3xl font-black leading-[1.1] tracking-[-0.02em]">
              {datosNegocio?.nombreUsuario
                ? `Bienvenido, ${datosNegocio.nombreUsuario.split(' ')[0]}.`
                : 'Bienvenido a Gastify.'}
              <br />
              ¿Cuál es el modelo de tu negocio?
            </h1>
          </div>


          {/* Error banner */}
          {errorMsg && (
            <div className="mb-6 flex items-start gap-2 rounded-xl bg-red-50 border border-red-100 p-3 text-red-600 text-[13px] font-semibold max-w-2xl mx-auto">
              <AlertCircle size={16} className="shrink-0 mt-0.5" />
              <span>{errorMsg}</span>
            </div>
          )}

          {/* Fallback: user sin datos del negocio (típico: org borrada por housekeeping) */}
          {needsBusinessForm && !provisioning && (
            <div className="mb-8 bg-[var(--surface)] rounded-3xl border border-[var(--gst-border-strong)] shadow-md p-6 max-w-xl mx-auto">
              <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-[var(--gst-mint-100)] text-[var(--gst-mint-700)] flex items-center justify-center shrink-0">
                  <Store size={18} />
                </div>
                <div>
                  <h3 className="text-[15px] font-black text-[var(--gst-graphite-900)]">
                    ¿Cómo se llama tu negocio?
                  </h3>
                  <p className="text-[12px] text-[var(--gst-graphite-600)] mt-0.5">
                    Es la info mínima que necesitamos para preparar tu cuenta.
                  </p>
                </div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] font-black uppercase tracking-[0.15em] text-[var(--gst-graphite-700)] mb-1.5 ml-1">
                    Nombre del negocio
                  </label>
                  <div className="relative">
                    <Store size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gst-graphite-400)] pointer-events-none" />
                    <input
                      type="text"
                      value={businessFormData.nombreNegocio}
                      onChange={(e) => setBusinessFormData((s) => ({ ...s, nombreNegocio: e.target.value }))}
                      placeholder="Ej: Minimarket El Sol"
                      className="w-full bg-[var(--surface)] border border-[var(--gst-border-strong)] text-[var(--gst-graphite-900)] rounded-xl pl-10 pr-4 py-2.5 text-[13px] font-semibold outline-none focus:border-[var(--gst-mint-400)] focus:ring-[3px] focus:ring-[var(--gst-mint-100)]"
                    />
                  </div>
                </div>
                <div>
                  <label className="flex items-center justify-between text-[10px] font-black uppercase tracking-[0.15em] text-[var(--gst-graphite-700)] mb-1.5 ml-1">
                    <span>WhatsApp</span>
                    <span className="text-[9px] font-bold normal-case tracking-normal text-[var(--gst-graphite-400)]">opcional</span>
                  </label>
                  <div className="relative">
                    <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gst-graphite-400)] pointer-events-none" />
                    <input
                      type="tel"
                      value={businessFormData.telefono}
                      onChange={(e) => setBusinessFormData((s) => ({ ...s, telefono: e.target.value }))}
                      placeholder="+54 9 11 1234 5678"
                      className="w-full bg-[var(--surface)] border border-[var(--gst-border-strong)] text-[var(--gst-graphite-900)] rounded-xl pl-10 pr-4 py-2.5 text-[13px] font-semibold outline-none focus:border-[var(--gst-mint-400)] focus:ring-[3px] focus:ring-[var(--gst-mint-100)]"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    const name = businessFormData.nombreNegocio.trim();
                    if (!name) {
                      setErrorMsg('Ingresá el nombre de tu negocio para continuar.');
                      return;
                    }
                    setErrorMsg(null);
                    const fullName = sessionUser?.user_metadata?.full_name || '';
                    setDatosNegocio({
                      nombreUsuario: fullName,
                      nombreNegocio: name,
                      telefono: businessFormData.telefono.trim(),
                      email: sessionUser?.email || '',
                    });
                    setNeedsBusinessForm(false);
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--gst-mint-500)] px-5 py-3 text-[13px] font-black uppercase tracking-[0.08em] text-white hover:bg-[var(--gst-mint-600)] transition-all shadow-md"
                >
                  Continuar <ArrowRight size={15} />
                </button>
              </div>
            </div>
          )}

          {/* Provisioning state */}
          {provisioning ? (
            <div className="bg-[var(--surface)] rounded-3xl border border-[var(--gst-border)] shadow-xl p-12 text-center max-w-md mx-auto">
              <div className="relative mb-6 inline-block">
                <div className="absolute inset-0 bg-[var(--gst-mint-200)] rounded-full blur-xl animate-pulse" />
                <div className="bg-[var(--surface)] border border-[var(--gst-border-strong)] rounded-full p-5 relative z-10 shadow-lg">
                  <Loader2 size={36} className="text-[var(--gst-mint-600)] animate-spin" strokeWidth={2.5} />
                </div>
              </div>
              <h2 className="gst-public-display text-2xl font-black mb-2">Activando tu prueba...</h2>
              <p className="text-[var(--gst-mint-700)] font-bold text-sm h-6 animate-pulse">{loadingMsg}</p>

              <div className="mt-8 space-y-3 text-left border border-[var(--gst-border)] rounded-2xl p-5 bg-[var(--gst-cream-50)]">
                {[
                  { key: 'auth', label: 'Cuenta de usuario' },
                  { key: 'org', label: 'Empresa creada' },
                  { key: 'final', label: 'Onboarding inicial listo' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center gap-3 text-sm font-bold">
                    <CheckCircle2 size={16} className={provisioningStatus[key] ? 'text-[var(--gst-mint-600)]' : 'text-[var(--gst-graphite-300)]'} />
                    <span className={provisioningStatus[key] ? 'text-[var(--gst-graphite-900)]' : 'text-[var(--gst-graphite-400)]'}>{label}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : needsBusinessForm ? null : (
            <>
              {/* ── Modelos de negocio (Tarjetas principales) ── */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
                {Object.values(presets).map((preset) => {
                  const Icon = PRESET_ICON_MAP[preset.key] || Store;
                  const isSelected = preset.key === businessPresetKey;
                  const highlights = preset.highlights || [
                    { title: 'Base Operativa', description: 'Módulos y categorías esenciales para arrancar.' },
                    { title: 'Configuración Rápida', description: 'Todo pre-armado y listo para empezar a vender.' }
                  ];

                  return (
                    <button
                      key={preset.key}
                      type="button"
                      onClick={() => setBusinessPresetKey(preset.key)}
                      className={`relative text-left rounded-3xl border-2 p-6 transition-all bg-[var(--surface)] flex flex-col justify-between ${
                        isSelected
                          ? 'border-[var(--gst-mint-500)] shadow-[0_20px_40px_rgba(16,185,129,0.18)] scale-[1.02]'
                          : 'border-[var(--gst-border-strong)] hover:border-[var(--gst-mint-300)] hover:shadow-md'
                      }`}
                    >
                      <div>
                        {/* Preset Icon & Tag */}
                        <div className="flex items-center justify-between gap-3 mb-4">
                          <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${
                            isSelected
                              ? 'bg-[var(--gst-mint-500)] text-white'
                              : 'bg-[var(--gst-cream-50)] text-[var(--gst-graphite-700)] border border-[var(--gst-border)]'
                          }`}>
                            <Icon size={20} />
                          </div>
                          {isSelected && (
                            <span className="inline-flex items-center rounded-full bg-[var(--gst-mint-100)] border border-[var(--gst-mint-200)] px-2.5 py-0.5 text-[9px] font-black uppercase tracking-[0.15em] text-[var(--gst-mint-700)]">
                              ✓ Elegido
                            </span>
                          )}
                        </div>

                        {/* Title and Description */}
                        <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[var(--gst-graphite-500)]">
                          {preset.shortLabel || preset.key}
                        </div>
                        <h3 className="mt-1 text-lg font-black text-[var(--gst-graphite-900)] leading-tight">
                          {preset.label}
                        </h3>
                        <p className="mt-2 text-[12px] font-medium text-[var(--gst-graphite-600)] leading-relaxed min-h-[60px]">
                          {preset.description}
                        </p>

                        {/* Highlights */}
                        <div className="mt-4 pt-4 border-t border-[var(--gst-border)] space-y-3">
                          {highlights.map((highlight, idx) => (
                            <div key={idx} className="flex items-start gap-2 text-[12px] text-[var(--gst-graphite-700)]">
                              <CheckCircle2 size={14} className="text-[var(--gst-mint-600)] shrink-0 mt-0.5" />
                              <div>
                                <strong className="font-black block leading-none">{highlight.title}</strong>
                                <span className="font-semibold text-[11px] text-[var(--gst-graphite-500)] leading-tight">{highlight.description}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                    </button>
                  );
                })}
              </div>

              {/* Trust line */}
              <div className="rounded-2xl bg-[var(--gst-mint-50,#ECFDF5)] border border-[var(--gst-mint-200)] p-4 mb-6 max-w-3xl mx-auto flex items-start gap-3">
                <ShieldCheck size={20} className="text-[var(--gst-mint-600)] shrink-0 mt-0.5" />
                <div>
                  <p className="text-[13px] font-black text-[var(--gst-mint-700)] mb-1">
                    Acceso Premium Completo sin Tarjeta
                  </p>
                  <p className="text-[12px] font-medium text-[var(--gst-graphite-700)]">
                    Tenés {formattedTrialDays} días de prueba gratis. Dejamos configurados tus datos base y los módulos de acuerdo a tu modelo de negocio. Podrás contratar o cambiar de plan manualmente cuando venza el período de prueba.
                  </p>
                </div>
              </div>

              {/* CTA */}
              <div className="max-w-2xl mx-auto">
                {(() => {
                  const selPreset = presets[businessPresetKey] || Object.values(presets)[0] || null;
                  return (
                    <button
                      type="button"
                      onClick={handleConfirmPlan}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-2xl bg-[var(--gst-mint-500)] px-6 py-4 text-sm font-black uppercase tracking-[0.1em] text-white shadow-[0_16px_32px_rgba(16,185,129,0.25)] transition hover:bg-[var(--gst-mint-600)] hover:shadow-[0_20px_40px_rgba(16,185,129,0.35)]"
                    >
                      <span className="sm:hidden">Empezar {formattedTrialDays} días gratis</span>
                      <span className="hidden sm:inline">Empezar mis {formattedTrialDays} días gratis con perfil {selPreset?.shortLabel || 'Negocio'}</span>
                      <ArrowRight size={18} />
                    </button>
                  );
                })()}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
